/*
 * Adapted from universe-react 5.10.9
 */

import React from 'react';
import PropTypes from 'prop-types';
import ForgePropTypes from '../utils/propTypes';
import { forgeClassHelper, stateClasses } from '../utils/classes';

const classes = forgeClassHelper('select');

const Select = React.forwardRef((props, ref) => {
  const {
    className,
    required,
    hideRequiredStyles,
    options,
    hasNullDefault,
    placeholder,
    children,
    disabled,
    error,
    ...rest
  } = props;

  return (
    <select
      {...classes({
        states: {
          disabled,
          error: error && !disabled,
          required: required && !hideRequiredStyles && !disabled,
        },
        extra: className,
      })}
      disabled={disabled}
      required={required}
      ref={ref}
      {...rest}
    >
      {hasNullDefault && (
        <option value="" key="fe_undefined">
          {placeholder}
        </option>
      )}
      {options &&
        options.map((option) =>
          option && typeof option === 'object' ? (
            <option
              value={option.value}
              key={option.value}
              disabled={option.disabled}
              className={option.disabled && stateClasses('disabled')}
            >
              {option.text ? option.text : option.value}
            </option>
          ) : (
            <option value={option} key={option}>
              {option}
            </option>
          )
        )}
      {children}
    </select>
  );
});

Select.displayName = 'Select';

/**
 * Select supports all the React select properties. Options can be set via the options prop, instead of as children.
 */
Select.propTypes = {
  /** Select options can be set through the options prop or through children, not both */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  /** Adds a class to the root element of the component */
  className: PropTypes.string,
  /** Determines the initial value of the select, but allows it to be updated (uncontrolled). */
  defaultValue: PropTypes.string,
  /** Impacts the styling of the select in addition to preventing selection */
  disabled: PropTypes.bool,
  /** Highlights the input when defined (specific string doesn't matter) */
  error: PropTypes.string,
  /** Specifies whether to include an explicit null option. Only unset this if there is a good default option */
  hasNullDefault: PropTypes.bool,
  /** Hides any required styling */
  hideRequiredStyles: PropTypes.bool,
  /** If an option is supplied as a primitive, it's both the value and the text of the option */
  options: ForgePropTypes.selectOptions,
  /** Text shown for the null option. Read the UX Content Guidelines before changing */
  placeholder: PropTypes.string,
  /** Indicates whether this select requires a non-null selection be chosen */
  required: PropTypes.bool,
  /** Value of the select. Creates a controlled component. */
  value: PropTypes.string,
};

Select.defaultProps = {
  disabled: false,
  hasNullDefault: true,
  hideRequiredStyles: false,
  placeholder: '- Select -',
  required: false,
};

export default Select;
